import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import './App.css';
import { Typography, Box, CssBaseline, Button, ThemeProvider, createTheme, Link, IconButton } from '@mui/material';
import { NavLink as RouterLink, Outlet, useLocation, Navigate } from 'react-router-dom';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import PolicyIcon from '@mui/icons-material/Policy';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ThemeToggleButton from './components/ThemeToggleButton';
import LinkIcon from '@mui/icons-material/Link';
import AppErrorBoundary from './components/ErrorBoundary';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CasesSidebar from './components/CasesSidebar';
import { handleLogout } from './auth/authUtils';
import { CasesProvider } from './contexts/CasesContext';
import { ToastProvider } from './components/Toast';
import { useTranslation } from 'react-i18next';
import { enUS, svSE } from '@mui/material/locale';
import LanguageSwitcher from './components/LanguageSwitcher';
import { getAppLanguage } from './i18n/i18n';
import { themeOptions } from './theme';

function App() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const isCompanyAssessment = location.pathname.startsWith('/companyassessment');
  const [hasLoggedOut, setHasLoggedOut] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const theme = useMemo(
    () => createTheme(themeOptions, getAppLanguage(i18n.resolvedLanguage) === 'sv' ? svSE : enUS),
    [i18n.resolvedLanguage]
  );
  
  // Check for intentional logout flag on component mount
  useEffect(() => {
    const wasIntentionalLogout = localStorage.getItem('intentional_logout') === 'true';
    
    if (wasIntentionalLogout) {
      setHasLoggedOut(true);
      localStorage.removeItem('intentional_logout');
    }
  }, []);

  // Removed auto-login logic - user must manually choose to log in

  const isMyceloApp = window.location.origin.includes('mycelo');

  // Helper function to create OIDC state with redirect URL
  const createAuthState = () => {
    const currentUrl = window.location.href;
    const isRootOrEmpty = window.location.pathname === '/' || window.location.pathname === '';
    
    if (currentUrl && !isRootOrEmpty && currentUrl.startsWith(window.location.origin)) {
      return { redirectUrl: currentUrl };
    }
    return undefined;
  };

  // Show logout success message
  if (hasLoggedOut && !auth.isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <div className="App">
          <CssBaseline />
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}><LanguageSwitcher /></Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '100vh',
            textAlign: 'center',
            padding: 'var(--spacing-xl)'
          }}>
            <Typography variant="h3" sx={{ 
              marginBottom: 'var(--spacing-lg)',
              fontWeight: 'var(--font-weight-bold)'
            }}>
              {t('auth.loggedOutTitle')}
            </Typography>
            <Typography variant="body1" sx={{ 
              marginBottom: 'var(--spacing-xl)',
            }}>
              {t('auth.loggedOutBody', { product: isMyceloApp ? 'Mycelo' : 'Breadify' })}
            </Typography>
            <Button 
              variant="contained"
              size="large"
              onClick={() => {
                // Clear the logout state
                setHasLoggedOut(false);
                
                // Force a fresh login by adding prompt=login parameter
                auth.signinRedirect({
                  extraQueryParams: {
                    prompt: 'login'
                  }
                });
              }} 
              sx={{
                backgroundColor: 'primary.main',
                padding: 'var(--spacing-md) var(--spacing-lg)',
                borderRadius: 'var(--border-radius-sm)',
                fontWeight: 'var(--font-weight-medium)',
                fontSize: 'var(--font-size-normal)',
                transition: 'all var(--transition-speed) var(--transition-easing)',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: 'var(--box-shadow-md)'
                }
              }}
            >
              {t('auth.loginAgain')}
            </Button>
          </Box>
        </div>
      </ThemeProvider>
    );
  }

  switch (auth.activeNavigator) {
    case 'signinSilent':
      return <div className="loading-message">{t('auth.signingIn')}</div>;
    case 'signoutRedirect':
      return <div className="loading-message">{t('auth.signingOut')}</div>;
  }

  if (auth.isLoading) {
    return <div className="loading-message">{t('auth.loading')}</div>;
  }

  if (auth.error) {
    return <div className="error-message">{t('auth.error', { message: auth.error.message })}</div>;
  }

  // Show login screen for unauthenticated users (instead of auto-redirecting)
  if (!auth.isAuthenticated && !auth.isLoading && !auth.error) {
    return (
      <ThemeProvider theme={theme}>
        <div className="App">
          <CssBaseline />
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}><LanguageSwitcher /></Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '100vh',
            textAlign: 'center',
            padding: 'var(--spacing-xl)'
          }}>
            <Typography variant="h2" sx={{ 
              marginBottom: 'var(--spacing-lg)',
              fontWeight: 'var(--font-weight-bold)'
            }}>
              {t('auth.welcome', { product: isMyceloApp ? 'Mycelo' : 'Breadify' })}
            </Typography>
            <Typography variant="body1" sx={{ 
              marginBottom: 'var(--spacing-xl)',
              maxWidth: '600px'
            }}>
              {t('auth.loginPrompt')}
            </Typography>
            <Button 
              variant="contained"
              size="large"
              onClick={() => {
                auth.signinRedirect({
                  state: createAuthState(),
                  extraQueryParams: {
                    prompt: 'login'
                  }
                });
              }} 
              sx={{
                backgroundColor: 'primary.main',
                padding: 'var(--spacing-md) var(--spacing-lg)',
                borderRadius: 'var(--border-radius-sm)',
                fontWeight: 'var(--font-weight-medium)',
                fontSize: 'var(--font-size-normal)',
                transition: 'all var(--transition-speed) var(--transition-easing)',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: 'var(--box-shadow-md)'
                }
              }}
            >
              {t('auth.login')}
            </Button>
          </Box>
        </div>
      </ThemeProvider>
    );
  }
  
  if (auth.isAuthenticated) {   
    return (
      <ThemeProvider theme={theme}>
        <ToastProvider>
        <CasesProvider>
          <div className="App">
            <CssBaseline />
            {/* Skip-to-content link for keyboard users */}
            <Box
              component="a"
              href="#main-content"
              sx={{
                position: 'absolute',
                left: '-9999px',
                top: 'auto',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
                '&:focus': {
                  position: 'fixed',
                  top: 8,
                  left: 8,
                  width: 'auto',
                  height: 'auto',
                  overflow: 'visible',
                  zIndex: 'tooltip',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  fontSize: 'var(--font-size-small)',
                  fontWeight: 'var(--font-weight-semibold)',
                  textDecoration: 'none',
                },
              }}
            >
              {t('navigation.skipToContent')}
            </Box>
            
            <Box sx={{ display: 'flex', height: '100vh' }}>
              {/* Sidebar */}
              <Box
                component="nav"
                aria-label={t('navigation.main')}
                className="sidebar"
                style={{
                  width: isSidebarCollapsed ? '60px' : '240px',
                  minWidth: isSidebarCollapsed ? '60px' : '240px',
                  maxWidth: isSidebarCollapsed ? '60px' : '240px',
                  boxSizing: 'border-box',
                }}
                sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  bgcolor: 'background.paper',
                  height: '100vh',
                  padding: isSidebarCollapsed ? 'var(--spacing-md) var(--spacing-xs)' : 'var(--spacing-lg)',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  transition: 'width 0.3s ease, padding 0.3s ease',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
              
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
                alignItems: 'center',
                marginBottom: isSidebarCollapsed ? 'var(--spacing-sm)' : 'var(--spacing-lg)',
                padding: isSidebarCollapsed ? 0 : 'var(--spacing-sm)'
              }}>
                {!isSidebarCollapsed && (
                  <Link component={RouterLink} to="/" sx={{ 
                    textDecoration: 'none', 
                    fontSize: 'var(--font-size-large)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: "text.primary",
                  }}>
                    {isMyceloApp ? 'Mycelo' : 'Breadify'}
                  </Link>
                )}
                
                <IconButton
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  size="small"
                  aria-label={t(isSidebarCollapsed ? 'navigation.expandSidebar' : 'navigation.collapseSidebar')}
                >
                  {isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
              </Box>
              
              {/* Navigation Links */}
              <Box className="sidebar-nav" sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                '& .sidebar-link': {
                  minHeight: 40,
                  color: 'text.secondary',
                  '& .MuiSvgIcon-root': { fontSize: 20 },
                  '&[aria-current="page"]': {
                    bgcolor: 'action.selected',
                    color: 'primary.main',
                    '& .MuiTypography-root': { fontWeight: 600 },
                  },
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                },
                gap: '4px',
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                paddingRight: '4px', // Avoid scrollbar covering content
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(128,128,128,0.3)', borderRadius: '4px' }
              }}>
                <Link component={RouterLink} to="/companyassessment" className="sidebar-link" title={t('navigation.assessmentTitle')} sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}>
                  <TroubleshootIcon />
                  {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('navigation.assessment')}</Typography>}
                </Link>
                <Link component={RouterLink} to="/shortestPath" className="sidebar-link" title={t('navigation.shortestPath')} sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}>
                  <CompareArrowsIcon />
                  {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('navigation.relationships')}</Typography>}
                </Link>
                <Link component={RouterLink} to="/personsearch" className="sidebar-link" title={t('navigation.personPositions')} sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}>
                  <PersonSearchIcon />
                  {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('navigation.personPositions')}</Typography>}
                </Link>
                  <Link component={RouterLink} to="/addresssearch" className="sidebar-link" title={t('navigation.addressSearch')} sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}>
                  <LocationOnIcon />
                  {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('navigation.addressSearch')}</Typography>}
                </Link>
                <Link component={RouterLink} to="/ManageWatchlist" className="sidebar-link" title={t('navigation.manageWatchlist')} sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}>
                  <PolicyIcon />
                  {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('navigation.watchlist')}</Typography>}
                </Link>
                <Link component={RouterLink} to="/graphmonitoring" className="sidebar-link" title={t('navigation.graphMonitoring')} sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}>
                  <AutoGraphIcon />
                  {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('navigation.monitoring')}</Typography>}
                </Link>
                <Link component={RouterLink} to="/CustomLinking" className="sidebar-link" title={t('navigation.customLinking')} sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}>
                  <LinkIcon />
                  {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('navigation.customLinking')}</Typography>}
                </Link>

                <Link component={RouterLink} to="/documentation" className="sidebar-link" title={t('navigation.documentation')} sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}>
                  <HelpOutlineIcon />
                  {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('navigation.documentation')}</Typography>}
                </Link>
                
                {/* Cases Section */}
                <CasesSidebar sidebarCollapsed={isSidebarCollapsed} />
              </Box>
              
              {/* Spacer removed as sidebar-nav is now flex: 1 */}
              
              {/* Logout Button */}
              <Button
                onClick={async () => {
                  try {
                    await handleLogout(auth, { clearLocalStorage: true });
                  } catch (error) {
                    console.error('Logout failed:', error);
                    // Fallback: redirect to home
                    window.location.href = window.location.origin;
                  }
                }}
                className="sidebar-link"
                variant="text"
                title={t('auth.logout')}
                sx={{
                  color: 'text.secondary',
                  backgroundColor: 'transparent',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  padding: isSidebarCollapsed ? '8px' : '8px 12px',
                  borderRadius: 'var(--border-radius-sm)',
                  transition: 'all var(--transition-speed)',
                  justifyContent: 'center',
                  textTransform: 'none',
                  minWidth: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  marginBottom: 'var(--spacing-md)',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}
              >
                <LogoutIcon />
                {!isSidebarCollapsed && <Typography variant="body2" sx={{ fontSize: 'var(--font-size-small)' }}>{t('auth.logout')}</Typography>}
              </Button>
              
              {/* Display preferences at bottom */}
              <Box
                data-testid="display-preferences"
                style={{
                  flexDirection: isSidebarCollapsed ? 'column' : 'row',
                  alignItems: 'center',
                  width: '100%',
                }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: isSidebarCollapsed ? 1 : 0.5,
                  minWidth: 0,
                  flexShrink: 0,
                  paddingTop: 'var(--spacing-md)',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}>
                <LanguageSwitcher compact={isSidebarCollapsed} />
                <ThemeToggleButton />
              </Box>
            </Box>

            {/* Main Content */}
            <Box
              id="main-content"
              component="main"
              className="main-content"
              sx={{
                marginLeft: isSidebarCollapsed ? '60px' : '240px',
                transition: 'margin-left 0.3s ease',
                flexGrow: 1,
                minWidth: 0,
                bgcolor: 'background.default',
                p: isLandingPage || isCompanyAssessment ? 0 : { xs: 2, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: isLandingPage ? 'center' : 'flex-start',
                justifyContent: isLandingPage ? 'center' : 'flex-start',
                minHeight: '100vh',
              }}
            >
              {isLandingPage ? (
                <Navigate to="/companyassessment" replace />
              ) : (
                <AppErrorBoundary>
                  {isCompanyAssessment ? <Outlet /> : <Box sx={{ width: '100%', minWidth: 0, maxWidth: 1600, mx: 'auto' }}><Outlet /></Box>}
                </AppErrorBoundary>
              )}
            </Box>
          </Box>
        </div>
        </CasesProvider>
        </ToastProvider>
      </ThemeProvider>
    );
  }

  // This should not be reached, but just in case
  return <div className="loading-message">{t('auth.loading')}</div>;
}

export default App;
